import { Group, Drawer, Burger, Menu, Accordion, Text } from '@mantine/core';
import { IconChevronDown, IconUserFilled } from '@tabler/icons-react';
import classes from './menu.module.css';
import { Link, useLocation } from 'react-router-dom';
import { rutas } from './rutas';
import { useState } from 'react';
import { useAutenticacion } from '../../contextos/authContext';
import { usePermisos } from '../../contextos/usePermisos';
import { EnumPermisos } from '../../enums/enumPermisos';
import { listaReportesGenerales } from '../../../mocks/reportes/reportesGenerales';
import { listaReportesCliente } from '../../../mocks/reportes/reportesCliente';

export const MenuModulos = () => {
  const location = useLocation();
  const { estaAutenticado, usuario } = useAutenticacion();
  const { puedeAccederModulo } = usePermisos();
  const [abrirMenuLateral, setAbrirMenuLateral] = useState<boolean>(false);

  if (!estaAutenticado) {
    return null;
  }

  // Determinar qué lista de reportes y ruta base usar según permisos
  const esReportesGenerales = puedeAccederModulo(EnumPermisos.PERMISO_REPORTES_GENERALES_VER);
  const listaReportes = esReportesGenerales ? listaReportesGenerales : listaReportesCliente;
  const rutaBaseReportes = esReportesGenerales ? '/Reportes' : '/Cliente/Reportes';

  // Filtrar por permisos individuales de cada reporte
  const listaReportesVisibles = listaReportes.filter(r => puedeAccederModulo(r.permisoRequerido));

  const esRutaReportesActiva = location.pathname === '/Reportes' || location.pathname === '/Cliente/Reportes';
  const queryParams = new URLSearchParams(location.search);
  const reporteActivo = queryParams.get('reporte') || listaReportesVisibles[0]?.slug || '';

  const items = rutas
    .filter((rutaFiltro) => rutaFiltro.Activa && puedeAccederModulo(rutaFiltro.PermisoRequerido))
    .map((ruta) => {
      const esReportes =
        ruta.Ruta === '/Reportes' || ruta.Ruta === '/Cliente/Reportes';

      // Evitar duplicado: si ya renderizamos el dropdown de reportes generales, omitir el de cliente
      const yaRenderizado =
        ruta.Ruta === '/Cliente/Reportes' && esReportesGenerales;
      if (yaRenderizado) return null;

      if (esReportes && listaReportesVisibles.length > 1) {
        return (
          <Menu
            key={ruta.NombreModulo}
            trigger="hover"
            openDelay={50}
            closeDelay={100}
            shadow="md"
            width={260}
            position="bottom-start"
            classNames={{ dropdown: classes.reportesDropdown }}
          >
            <Menu.Target>
              <span
                className={`${classes.link} ${esRutaReportesActiva ? classes.active : ''}`}
                style={{ cursor: 'pointer' }}
              >
                {ruta.Icono}
                {ruta.NombreModulo}
                <IconChevronDown size={14} />
              </span>
            </Menu.Target>
            <Menu.Dropdown>
              {listaReportesVisibles.map((reporte) => {
                const esActivo = reporteActivo === reporte.slug && esRutaReportesActiva;
                return (
                  <Menu.Item
                    key={reporte.slug}
                    component={Link}
                    to={`${rutaBaseReportes}?reporte=${reporte.slug}`}
                    onClick={() => setAbrirMenuLateral(false)}
                    className={esActivo ? `${classes.reportesItem} ${classes.reportesItemActivo}` : classes.reportesItem}
                  >
                    <Group gap={10} wrap="nowrap" align="flex-start">
                      {reporte.icono && (
                        <span className={classes.reportesItemIcono}>{reporte.icono}</span>
                      )}
                      <div>
                        <div className={classes.reportesItemNombre}>{reporte.nombre}</div>
                        {reporte.descripcion && (
                          <div className={classes.reportesItemDesc}>{reporte.descripcion}</div>
                        )}
                      </div>
                    </Group>
                  </Menu.Item>
                );
              })}
            </Menu.Dropdown>
          </Menu>
        );
      }

      // Solo un reporte visible: link directo sin dropdown
      if (esReportes && listaReportesVisibles.length === 1) {
        return (
          <Link
            key={ruta.NombreModulo}
            to={`${rutaBaseReportes}?reporte=${listaReportesVisibles[0].slug}`}
            className={`${classes.link} ${esRutaReportesActiva ? classes.active : ''}`}
            onClick={() => setAbrirMenuLateral(false)}
          >
            {ruta.Icono}
            {ruta.NombreModulo}
          </Link>
        );
      }

      return (
        <Link
          key={ruta.NombreModulo}
          to={ruta.Ruta}
          className={`${classes.link} ${location.pathname === ruta.Ruta ? classes.active : ''}`}
          onClick={() => setAbrirMenuLateral(false)}
        >
          {ruta.Icono}
          {ruta.NombreModulo}
        </Link>
      );
    });

  // Renderizar navegación del Drawer (Mobile)
  const renderDrawerNav = () => {
    return rutas
      .filter((rutaFiltro) => rutaFiltro.Activa && puedeAccederModulo(rutaFiltro.PermisoRequerido))
      .map((ruta) => {
        const esReportes = ruta.Ruta === '/Reportes' || ruta.Ruta === '/Cliente/Reportes';
        const yaRenderizado = ruta.Ruta === '/Cliente/Reportes' && esReportesGenerales;
        if (yaRenderizado) return null;

        if (esReportes && listaReportesVisibles.length > 1) {
          return (
            <Accordion key={ruta.NombreModulo} variant="default" className={classes.drawerAccordion}>
              <Accordion.Item value="reportes" style={{ border: 'none', backgroundColor: 'transparent' }}>
                <Accordion.Control
                  className={`${classes.drawerAccordionControl} ${esRutaReportesActiva ? classes.drawerLinkActive : ''}`}
                  icon={<span className={classes.drawerAccordionIcon}>{ruta.Icono}</span>}
                >
                  {ruta.NombreModulo}
                </Accordion.Control>
                <Accordion.Panel>
                  {listaReportesVisibles.map((reporte) => {
                    const esActivo = reporteActivo === reporte.slug && esRutaReportesActiva;
                    return (
                      <Link
                        key={reporte.slug}
                        to={`${rutaBaseReportes}?reporte=${reporte.slug}`}
                        className={`${classes.drawerLink} ${classes.drawerSubLink} ${esActivo ? classes.drawerSubLinkActive : ''}`}
                        onClick={() => setAbrirMenuLateral(false)}
                      >
                        <Group gap={10} wrap="nowrap">
                          {reporte.icono && (
                            <span className={classes.reportesItemIcono} style={{ display: 'flex', fontSize: '1.1rem' }}>
                              {reporte.icono}
                            </span>
                          )}
                          <Text size="sm">{reporte.nombre}</Text>
                        </Group>
                      </Link>
                    );
                  })}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          );
        }

        const esActivo = location.pathname === ruta.Ruta;
        return (
          <Link
            key={ruta.NombreModulo}
            to={ruta.Ruta}
            className={`${classes.drawerLink} ${esActivo ? classes.drawerLinkActive : ''}`}
            onClick={() => setAbrirMenuLateral(false)}
          >
            <span className={classes.drawerLinkIcon}>{ruta.Icono}</span>
            <Text>{ruta.NombreModulo}</Text>
          </Link>
        );
      });
  };

  return (
    <header className={classes.headerMenuModulos}>
      <div className={classes.inner}>
        <Burger
          opened={abrirMenuLateral}
          onClick={() => setAbrirMenuLateral((v) => !v)}
          size="sm"
          className={classes.burger}
        />

        <Group ml={50} gap={5} className={classes.links}>
          {items}
        </Group>

        <Drawer
          opened={abrirMenuLateral}
          onClose={() => setAbrirMenuLateral(false)}
          padding="md"
          size="xs"
          title={<Text fw={700} size="lg">Menú</Text>}
          zIndex={10000}
        >
          <div className={classes.drawerWrapper}>
            <div>
              <div className={classes.drawerSectionTitle}>Módulos</div>
              <nav className={classes.drawerNav}>
                {renderDrawerNav()}
              </nav>
            </div>

            <div style={{ marginTop: 'auto' }}>
              <div style={{ padding: '20px 16px', borderTop: '1px solid var(--mantine-color-gray-2)', margin: '0 16px' }}>
                <Group gap="sm">
                  <IconUserFilled color="var(--mantine-color-primary-0)" size={24} />
                  <div style={{ flex: 1 }}>
                    <Text size="sm" fw={700} style={{ color: 'var(--mantine-color-gray-8)' }}>{usuario?.NombreCompleto}</Text>
                    <Text size="xs" color="dimmed">Sesión activa</Text>
                  </div>
                </Group>
              </div>

              <div className={classes.logoDrawer}>
                <img
                  src="/logo-conekta-smart-mesa-trabajo-1.png"
                  width={120}
                  alt="Logo Footer"
                />
              </div>
            </div>
          </div>
        </Drawer>
      </div>
    </header>
  );
};